import type { ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import { ADBUILDING, NEON, SAKURA, TOKYO_NIGHT_GLASS, TOKYO_TIMBER } from '../palettes';

/**
 * scenery/zones/tokyo.ts — neon night. Electric Shibuya ad-canyons → the low
 * lantern-lit timber streets of Asakusa (cherry blossom, temple roofs) → the
 * office towers of Minato under Tokyo Tower → the ink-dark bay to the finish.
 * The bay lies on side +1.
 */

export const TOKYO_BAY_SIDE = 1;

/** cities.ts: shibuyaTower rides side +1 (the minato/tokyoTower precedent —
 *  a zone mirrors its landmark's side as a local constant). */
const SHIBUYA_TOWER_SIDE = 1;
/** Forecourt half-window in progress units (≈ ±44 u of the 2192 u course):
 *  the rebuilt 56 u tower + its base mega-screen get an open plaza. */
const TOWER_CLEAR = 0.02;

/** tokyo-ts: the NEON palette's pure white renders as a BLOWN featureless
 *  rectangle on the full-bright slab material (measured at the ~0.87 wharf —
 *  ramp-chase QA shot). Remap it to the city's cyan; colour-only, the rng
 *  draw that picked it is untouched. */
const neonColor = (c: number): number => (c === 0xffffff ? 0x18d0ff : c);

const shibuya: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // tokyo-ts forecourt clear: the rebuilt Shibuya-109 (height 56, curved
  // corner mega-screen) was buried at its base behind its own kerb's
  // billboard blocks from most road angles. Inside the window on the TOWER'S
  // side every rng draw below still happens — only the pushes are discarded
  // (the kaali-peeli skip precedent, byte-safe outside the window) — so the
  // tower stands on a readable plaza while the far kerb keeps the ad canyon.
  const towerAt = marks.shibuyaTower;
  const clear =
    towerAt != null && side === SHIBUYA_TOWER_SIDE && Math.abs(progress - towerAt) < TOWER_CLEAR;
  // Dark towers whose road faces ARE the light: giant ad screens or vertical
  // glyph sign-columns, with accent neon slabs between. Remember this step's
  // facade plane — the accent slabs mount flush to it (they used to hang at a
  // fixed kerb offset, floating in mid-air up to ~4 u off the wall).
  let face: { lat: number; h: number } | null = null;
  if (rng.next() < 0.93) {
    const w = rng.range(10, 17);
    const h = rng.range(20, 44);
    const d = rng.range(9, 14);
    const lat = hw + 2 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    const color = rng.pick(ADBUILDING);
    face = { lat: lat - w / 2, h };
    if (!clear) a.adBuildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    if (rng.next() < 0.5) {
      const [sx, , sz] = at(lat - w / 2 - 0.25);
      const tex = rng.int(0, 6);
      if (!clear) a.adScreens.push({ x: sx, y: s.y + h * 0.55, z: sz, rotY: s.dir, w: w * 0.9, h: h * 0.6, tex });
    } else {
      const [vx, , vz] = at(lat - w / 2 - 0.5);
      const tex = rng.int(0, 4);
      if (!clear) a.vertSigns.push({ x: vx, y: s.y + h * 0.42, z: vz, rotY: s.dir, w: 2.4, h: h * 0.6, tex });
    }
  }
  if (rng.next() < 0.5) {
    const yOff = rng.range(5, 20);
    const w = rng.range(3, 6);
    const h = rng.range(3, 6);
    const color = neonColor(rng.pick(NEON));
    // Mount slightly embedded in this step's facade (centre 0.25 inside the
    // wall plane → the 0.6-thick panel stands 0.35 proud, no air gap). A step
    // that drew no billboard tower keeps its draws but hangs no slab —
    // there's nothing to mount on. Height clamps under the tower's roofline.
    if (!clear && face) {
      const [nx, , nz] = at(face.lat - 0.25);
      a.neon.push({
        x: nx,
        y: s.y + Math.min(yOff, face.h - h / 2 - 1),
        z: nz,
        rotY: s.dir,
        w,
        h,
        color,
      });
    }
  }
  // The famous Shibuya scramble — packed (kept inside the forecourt window:
  // a crowd at the 109's feet is the point, and it occludes nothing).
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.4, hw + 6], chance: 0.9, count: 5 });
};

const asakusa: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, progress, marks }) => {
  // Keep the great gate's forecourt clear so Kaminarimon stands alone.
  const gateAt = marks.kaminarimon;
  if (gateAt != null && Math.abs(progress - gateAt) < 0.018) return;
  // Low timber machiya rows under heavy dark tile roofs, paper-warm windows.
  if (rng.next() < 0.88) {
    const w = rng.range(7, 11);
    const h = rng.range(4.5, 8);
    const d = rng.range(8, 12);
    const lat = hw + 2 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color: rng.pick(TOKYO_TIMBER) });
    a.roofs.push({ x, y: y + h, z, rotY: s.dir, w: w + 2.4, h: 2.4, d: d + 2.4, color: 0x2e3138 });
    // The machiya signatures (M14): a dark kōshi LATTICE shopfront band at
    // street level and a tiled mid-eave over it (the two-step facade every
    // old Tokyo street wears). Some doorways get a split noren curtain.
    const [lx, ly, lz] = at(lat - w / 2 - 0.14);
    a.plain.push({ x: lx, y: ly + 1.25, z: lz, rotY: s.dir, w: 0.22, h: 2.5, d: d * 0.8, color: 0x1f1a13 }); // kōshi lattice
    a.plain.push({ x: lx, y: ly + 2.85, z: lz, rotY: s.dir, w: 1.5, h: 0.28, d: d * 0.88, color: 0x2e3138 }); // mid-eave
    if (rng.next() < 0.4) {
      const [nx, ny, nz] = at(lat - w / 2 - 0.3);
      a.plain.push({ x: nx, y: ny + 2.05, z: nz, rotY: s.dir, w: 0.1, h: 1.0, d: 1.9, color: rng.pick([0x223a6a, 0x6a2230, 0x2a4a3a]) }); // noren
    }
  }
  // Red lanterns glowing along the kerb.
  if (rng.next() < 0.34) {
    const [x, y, z] = at(hw + 1.7);
    a.lanterns.push({ x, y: y + 3.3, z, rotY: s.dir, s: rng.range(0.85, 1.25) });
  }
  // Cherry blossom trees between the houses.
  if (rng.next() < 0.26) {
    const [x, y, z] = at(hw + 3 + rng.range(0, 4));
    a.trees.push({ x, y, z, s: rng.range(0.8, 1.3), color: rng.pick(SAKURA) });
  }
  // Temple-town visitors browsing the Nakamise approach.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 4.5], chance: 0.65, count: 3 });
};

const minato: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // Tokyo Tower gets a plaza: no towers at its feet, lower ones opposite.
  const towerAt = marks.tokyoTower;
  const nearTower = towerAt != null && Math.abs(progress - towerAt) < 0.028;
  if (nearTower && side === -1) return;
  if (rng.next() < 0.88) {
    const w = rng.range(9, 16);
    const h = nearTower ? rng.range(14, 26) : rng.range(18, 48);
    const [x, y, z] = at(hw + 2.5 + w / 2 + rng.range(0, 5));
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(9, 15), color: rng.pick(TOKYO_NIGHT_GLASS) });
  }
  if (rng.next() < 0.22) {
    const [vx, , vz] = at(hw + 2.6);
    a.vertSigns.push({ x: vx, y: s.y + rng.range(6, 14), z: vz, rotY: s.dir, w: 1.8, h: rng.range(8, 14), tex: rng.int(0, 4) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 5], chance: 0.5, count: 3 });
};

const bayfinish: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === TOKYO_BAY_SIDE) {
    // The ink-dark bay, wide and quiet.
    const w = rng.range(60, 80);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: 0x0e2236, arc: s.distance, side, shore: hw + 1 });
  } else {
    // Wharf side: dark sheds with the odd neon accent.
    // tokyo-ts: sign slabs mount on the shed that spawned this step (they
    // floated at the kerb line — and the white pick read as a giant blown
    // rectangle beside the ~0.87 launch ramp, the reported QA find).
    let face: { lat: number; h: number } | null = null;
    if (rng.next() < 0.55) {
      const w = rng.range(10, 16);
      const h = rng.range(6, 12);
      const lat = hw + 4 + w / 2 + rng.range(0, 5);
      const [x, y, z] = at(lat);
      const d = rng.range(10, 16);
      const color = rng.pick(ADBUILDING);
      face = { lat: lat - w / 2, h };
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    }
    if (rng.next() < 0.3) {
      const yOff = rng.range(4, 10);
      const w = rng.range(3, 5);
      const h = rng.range(2, 4);
      const color = neonColor(rng.pick(NEON));
      if (face) {
        const [nx, , nz] = at(face.lat - 0.25);
        a.neon.push({
          x: nx,
          y: s.y + Math.min(yOff, face.h - h / 2 - 0.5),
          z: nz,
          rotY: s.dir,
          w,
          h,
          color,
        });
      }
    }
    // A few dockworkers/onlookers on the wharf (never out over the bay).
    scatterPeople({ peopleRng, at, a }, { band: [hw + 2, hw + 5], chance: 0.3, count: 2 });
  }
};

export const TOKYO_ZONES = { shibuya, asakusa, minato, bayfinish };
